import { Clock } from "lucide-react";
import { SG7TacticalPanel } from "./sg7";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface KeyMinutesCardProps {
  minutosChave?: Record<string, Record<string, string>>;
  homeTeamName?: string;
  awayTeamName?: string;
  className?: string;
}

export function KeyMinutesCard({ 
  minutosChave, 
  homeTeamName,
  awayTeamName,
  className 
}: KeyMinutesCardProps) {
  const { t } = useTranslation('analysis');
  
  // Use translated defaults if team names not provided
  const home = homeTeamName || t('defaults.home');
  const away = awayTeamName || t('defaults.away');

  if (!minutosChave) return null;

  const homeData = minutosChave.home || minutosChave[home];
  const awayData = minutosChave.away || minutosChave[away];

  if (!homeData && !awayData) return null;

  const renderMinutes = (data: Record<string, unknown>, teamName: string) => {
    if (!data || typeof data !== 'object') return null;
    
    // Flatten nested structures: { metric: { minute: value } } → flat array
    const flatEntries: Array<{ label: string; minute: string; value: string }> = [];
    
    Object.entries(data).forEach(([key, val]) => {
      if (typeof val === 'string') {
        // Simple format: { "61-75": "30%" }
        flatEntries.push({ label: '', minute: key, value: val });
      } else if (typeof val === 'object' && val !== null) {
        // Nested format: { gols_sofridos: { "76-90": "20%" } }
        Object.entries(val as Record<string, unknown>).forEach(([minute, pct]) => {
          if (typeof pct === 'string' || typeof pct === 'number') {
            flatEntries.push({ 
              label: key.replace(/_/g, ' '), 
              minute, 
              value: String(pct) 
            });
          }
        });
      }
    });
    
    if (flatEntries.length === 0) return null;

    return (
      <div className="space-y-1">
        <span className="text-sm font-medium text-[hsl(var(--sg7-text))]">{teamName}</span>
        <div className="flex flex-wrap gap-1">
          {flatEntries.map((entry, idx) => (
            <span 
              key={idx}
              className={cn(
                "text-xs px-2 py-1 rounded",
                "bg-[hsl(var(--sg7-surface-2))]",
                "border border-[hsl(var(--sg7-stroke-subtle))]",
                "text-[hsl(var(--sg7-text-muted))]"
              )}
            >
              {entry.label ? `${entry.label}: ` : ''}{entry.minute} → {entry.value}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <SG7TacticalPanel className={className}>
      <div className="p-4">
        <h3 className="text-sm font-bold flex items-center gap-2 text-[hsl(var(--sg7-text))] mb-3">
          <Clock className="h-4 w-4 text-[hsl(var(--sg7-gold))]" />
          {t('cards.keyMinutes')}
        </h3>
        <div className="space-y-3">
          {homeData && renderMinutes(homeData, home)}
          {awayData && renderMinutes(awayData, away)}
        </div>
      </div>
    </SG7TacticalPanel>
  );
}
